import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { HoverLift, GradientShift } from '../ui/motion-primitives';

interface PricingTier {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  href: string;
  popular?: boolean;
  variant: 'default' | 'popular' | 'enterprise';
}

interface PricingTeaserProps {
  // Content Props
  title?: string;
  subtitle?: string;
  
  // Display Props
  tiers?: PricingTier[];
  showPopularBadge?: boolean;
  
  // Style Props
  className?: string;
  variant?: 'default' | 'compact';
  
  // Animation Props
  enableAnimations?: boolean;
  staggerDelay?: number;
  
  // Behavior Props
  onTierClick?: (tier: PricingTier) => void;
  showFullPricingLink?: boolean;
  showSectionHeader?: boolean;
}

const PricingTeaser: React.FC<PricingTeaserProps> = ({
  title,
  subtitle,
  tiers,
  showPopularBadge = true,
  className = "",
  variant = 'default',
  enableAnimations = true,
  staggerDelay = 0.1,
  onTierClick,
  showFullPricingLink = true,
  showSectionHeader = true,
  ...props
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  // Motion components
  const MotionSection = enableAnimations ? motion.section : 'section';
  const MotionDiv = enableAnimations ? motion.div : 'div';

  // Animation variants (defined early for use in compact variant)
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    },
    hover: { 
      y: -4,
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  // Default content with i18n
  const defaultTitle = title || t('pricing.title', { ns: 'homepage' });
  const defaultSubtitle = subtitle || t('pricing.subtitle', { ns: 'homepage' });

  // Default tiers (defined early for use in compact variant)
  const defaultTiers: PricingTier[] = tiers || [
    {
      id: 'free',
      name: t('pricing.tiers.free.name', { ns: 'homepage' }),
      price: '$0',
      period: t('pricing.tiers.free.period', { ns: 'homepage' }),
      description: t('pricing.tiers.free.description', { ns: 'homepage' }),
      features: [
        t('pricing.tiers.free.features.memories', { ns: 'homepage' }),
        t('pricing.tiers.free.features.spaces', { ns: 'homepage' }),
        t('pricing.tiers.free.features.voice', { ns: 'homepage' })
      ],
      cta: t('pricing.tiers.free.cta', { ns: 'homepage' }),
      href: '/signup',
      variant: 'default'
    },
    {
      id: 'starter',
      name: t('pricing.tiers.starter.name', { ns: 'homepage' }),
      price: '$9',
      period: t('pricing.tiers.starter.period', { ns: 'homepage' }),
      description: t('pricing.tiers.starter.description', { ns: 'homepage' }),
      features: [
        t('pricing.tiers.starter.features.memories', { ns: 'homepage' }),
        t('pricing.tiers.starter.features.spaces', { ns: 'homepage' }),
        t('pricing.tiers.starter.features.api', { ns: 'homepage' }),
        t('pricing.tiers.starter.features.voice', { ns: 'homepage' })
      ],
      cta: t('pricing.tiers.starter.cta', { ns: 'homepage' }),
      href: '/billing/upgrade',
      popular: true,
      variant: 'popular'
    },
    {
      id: 'pro',
      name: t('pricing.tiers.pro.name', { ns: 'homepage' }),
      price: '$29',
      period: t('pricing.tiers.pro.period', { ns: 'homepage' }),
      description: t('pricing.tiers.pro.description', { ns: 'homepage' }),
      features: [
        t('pricing.tiers.pro.features.unlimited', { ns: 'homepage' }),
        t('pricing.tiers.pro.features.multimodal', { ns: 'homepage' }),
        t('pricing.tiers.pro.features.api', { ns: 'homepage' }),
        t('pricing.tiers.pro.features.support', { ns: 'homepage' })
      ],
      cta: t('pricing.tiers.pro.cta', { ns: 'homepage' }),
      href: '/billing/upgrade',
      variant: 'enterprise'
    }
  ];

  // Simplified pricing overview for reduced prominence
  if (variant === 'compact') {
    return (
      <MotionSection
        variants={enableAnimations ? itemVariants : undefined}
        initial={enableAnimations ? "hidden" : undefined}
        animate={enableAnimations ? "visible" : undefined}
        className={cn("bg-muted/20 py-8 md:py-12 px-4 md:px-8", className)}
        {...props}
      >
        <div className="text-center space-y-4 md:space-y-6 max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {t('pricing.simpleTitle', { ns: 'homepage' })}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
            {t('pricing.simpleSubtitle', { ns: 'homepage' })}
          </p>

          {/* Simplified pricing overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
            {defaultTiers.slice(0, 3).map((tier) => (
              <div key={tier.id} className="bg-background rounded-lg p-4 md:p-6 border">
                <div className="text-center space-y-2">
                  <h3 className="font-semibold text-foreground">{tier.name}</h3>
                  <div className="text-2xl font-bold text-primary">{tier.price}</div>
                  <div className="text-xs text-muted-foreground">/{tier.period}</div>
                  <p className="text-xs text-muted-foreground mt-2">{tier.description}</p>
                </div>
              </div>
            ))}
          </div>

          {showFullPricingLink && (
          <div className="pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.assign('/pricing')}
              className="h-9 px-4 text-sm"
            >
              {t('pricing.viewFullPricing', { ns: 'homepage' })}
              <ArrowRight className="w-3 h-3 ml-2" />
            </Button>
          </div>
          )}
        </div>
      </MotionSection>
    );
  }

  // Handle tier clicks
  const handleTierClick = (tier: PricingTier) => {
    console.log('PricingTeaser: Tier clicked', { 
      tierId: tier.id, 
      href: tier.href, 
      isAuthenticated 
    });
    
    if (onTierClick) {
      onTierClick(tier);
    } else {
      // Route based on authentication status
      let targetUrl: string;
      
      if (!isAuthenticated) {
        targetUrl = '/signup';
      } else {
        // Authenticated users can go to their intended destination
        if (tier.id === 'free') {
          // Free tier users go to dashboard (they're already signed up)
          targetUrl = '/dashboard';
        } else {
          // Paid tier users go to billing upgrade
          targetUrl = '/billing/upgrade';
        }
      }
      
      console.log('PricingTeaser: Navigating to', targetUrl);
      window.location.assign(targetUrl);
    }
  };

  // Generate schema.org structured data for pricing
  const generatePricingSchema = () => {
    const offers = defaultTiers.map(tier => ({
      "@type": "Offer",
      "name": tier.name,
      "price": tier.price.replace('$', ''),
      "priceCurrency": "USD",
      "description": tier.description,
      "category": tier.id === 'free' ? 'Free' : 'Paid',
      "eligibleQuantity": {
        "@type": "QuantitativeValue",
        "value": 'unlimited',
        "unitText": "memories"
      }
    }));

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Memara AI Memory System",
      "description": "Modular memory system for AI agents and workflows",
      "brand": {
        "@type": "Brand",
        "name": "Memara"
      },
      "offers": offers,
      "category": "Software",
      "applicationCategory": "AI Tools",
      "operatingSystem": "Web-based"
    };
  };

  // Variant classes
  const variantClasses = {
    default: "space-y-6 md:space-y-8 py-12 md:py-16 px-4 md:px-8",
    compact: "space-y-4 md:space-y-6 py-8 md:py-12 px-4 md:px-6"
  };

  // Tier styling
  const getTierClasses = (tier: PricingTier) => {
    switch (tier.variant) {
      case 'popular':
        return {
          card: "border-primary shadow-lg scale-105 relative",
          header: "bg-primary text-primary-foreground",
          badge: "bg-primary text-primary-foreground",
          button: "bg-primary hover:bg-primary/90 text-primary-foreground",
          text: "text-primary-foreground",
          description: "text-primary-foreground",
          feature: "text-foreground/90"
        };
      case 'enterprise':
        return {
          card: "border-purple-200 hover:border-purple-300",
          header: "bg-gradient-to-r from-purple-50 to-indigo-50",
          badge: "bg-purple-100 text-purple-700",
          button: "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white",
          text: "text-foreground",
          description: "text-foreground/80",
          feature: "text-foreground/90"
        };
      default:
        return {
          card: "border-border hover:border-primary/20",
          header: "bg-muted/50",
          badge: "bg-muted text-muted-foreground",
          button: "bg-muted hover:bg-muted/80 text-foreground",
          text: "text-foreground",
          description: "text-foreground/80",
          feature: "text-foreground/90"
        };
    }
  };

  return (
    <>
      {/* Schema.org structured data for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generatePricingSchema())}
        </script>
      </Helmet>

      <MotionSection 
        variants={enableAnimations ? itemVariants : undefined}
        initial={enableAnimations ? "hidden" : undefined}
        animate={enableAnimations ? "visible" : undefined}
        className={cn(variantClasses[variant], "bg-muted/20", className)}
        {...props}
      >
      {showSectionHeader && (
      <div className="text-center space-y-3 md:space-y-4 px-4 md:px-0">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">{defaultTitle}</h2>
        <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
          {defaultSubtitle}
        </p>
        <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
          {t('pricing.promoNote', { ns: 'homepage' })}
        </p>
      </div>
      )}

      {/* Pricing Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto px-4 md:px-0">
        {defaultTiers.map((tier, index) => {
          const tierClasses = getTierClasses(tier);
          
          return (
            <MotionDiv
              key={tier.id}
              variants={enableAnimations ? cardVariants : undefined}
              initial={enableAnimations ? "hidden" : undefined}
              animate={enableAnimations ? "visible" : undefined}
              transition={enableAnimations ? { delay: index * staggerDelay } : undefined}
              className="group"
            >
              <HoverLift liftAmount={6}>
                <Card className={cn(
                  "h-full transition-all duration-300 hover:shadow-lg",
                  tierClasses.card
                )}>
                {/* Popular Badge */}
                {tier.popular && showPopularBadge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className={cn("px-3 py-1 text-xs font-semibold", tierClasses.badge)}>
                      {t('pricing.popularBadge', { ns: 'homepage' })}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className={cn("text-center pb-4", tierClasses.header)}>
                  <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
                    <CardTitle className={cn("text-xl", tierClasses.text)}>{tier.name}</CardTitle>
                  </GradientShift>
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={cn("text-3xl font-bold", tierClasses.text)}>{tier.price}</span>
                      <span className={cn("text-sm", tierClasses.description)}>/{tier.period}</span>
                    </div>
                    <CardDescription className={tierClasses.description}>{tier.description}</CardDescription>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Features List */}
                  <div className="space-y-3">
                    {tier.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className={cn("text-sm", tierClasses.feature)}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4">
                    <Button 
                      className={cn("w-full h-11 md:h-10", tierClasses.button)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleTierClick(tier);
                      }}
                    >
                      {tier.cta}
                      {tier.popular ? (
                        <Sparkles className="w-4 h-4 ml-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 ml-2" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </HoverLift>
            </MotionDiv>
          );
        })}
      </div>

      {showFullPricingLink && (
      <div className="text-center pt-6 md:pt-8 px-4 md:px-0">
        <Button 
          variant="outline" 
          size="lg"
          onClick={() => window.location.assign('/pricing')}
          className="h-12 px-6 md:px-8 text-base md:text-lg font-medium min-w-[200px]"
        >
          {t('pricing.viewFullPricing', { ns: 'homepage' })}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
      )}
    </MotionSection>
    </>
  );
};

export default PricingTeaser;
export type { PricingTeaserProps, PricingTier };
